import { useEffect, useMemo, useState } from 'react'

import { makeStyles } from '@material-ui/core/styles'

import { ActionSelections } from '../../../core/types/chat.types'
import FlowActionInlineForm from './flow-action-inline-form'

type FlowActionYearRangeFormProps = {
  title: string
  initialYear1?: number
  initialYear2?: number
  onCancel: () => void
  onApply: (selections: ActionSelections) => void
}

const isValidYear = (value?: number): boolean => typeof value === 'number' && value >= 2000 && value <= 2100

const useStyles = makeStyles((theme) => ({
  grid: {
    display: 'grid',
    gap: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'var(--ds-fg-secondary, #535b6c)',
  },
  input: {
    width: '100%',
    borderRadius: 10,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    color: 'var(--ds-fg-primary, #1f2430)',
    padding: theme.spacing(1.25, 1.5),
    fontSize: '0.875rem',
    lineHeight: 1.5,
    outline: 'none',
    boxSizing: 'border-box',
  },
}))

const FlowActionYearRangeForm = ({
  title,
  initialYear1,
  initialYear2,
  onCancel,
  onApply,
}: FlowActionYearRangeFormProps) => {
  const classes = useStyles()
  const [year1, setYear1] = useState('')
  const [year2, setYear2] = useState('')

  useEffect(() => {
    setYear1(isValidYear(initialYear1) ? String(initialYear1) : '')
    setYear2(isValidYear(initialYear2) ? String(initialYear2) : '')
  }, [initialYear1, initialYear2])

  const parsedYear1 = useMemo(() => {
    const parsed = Number.parseInt(year1, 10)
    return Number.isNaN(parsed) ? undefined : parsed
  }, [year1])
  const parsedYear2 = useMemo(() => {
    const parsed = Number.parseInt(year2, 10)
    return Number.isNaN(parsed) ? undefined : parsed
  }, [year2])

  const canApply = isValidYear(parsedYear1) && isValidYear(parsedYear2)

  const handleApply = () => {
    if (!canApply || parsedYear1 === undefined || parsedYear2 === undefined) {
      return
    }

    onApply({
      year1: parsedYear1,
      year2: parsedYear2,
    })
  }

  return (
    <FlowActionInlineForm title={title} canApply={canApply} onCancel={onCancel} onApply={handleApply}>
      <div className={classes.grid}>
        <div className={classes.field}>
          <label className={classes.label} htmlFor="flow-year-1">
            Year 1
          </label>
          <input
            id="flow-year-1"
            className={classes.input}
            type="number"
            min={2000}
            max={2100}
            placeholder="YYYY"
            value={year1}
            onChange={(event) => setYear1(event.target.value)}
          />
        </div>

        <div className={classes.field}>
          <label className={classes.label} htmlFor="flow-year-2">
            Year 2
          </label>
          <input
            id="flow-year-2"
            className={classes.input}
            type="number"
            min={2000}
            max={2100}
            placeholder="YYYY"
            value={year2}
            onChange={(event) => setYear2(event.target.value)}
          />
        </div>
      </div>
    </FlowActionInlineForm>
  )
}

export default FlowActionYearRangeForm
